import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, Crown, MessageSquare, FlaskConical, Stethoscope, HelpCircle, CheckSquare, Library, Loader2, AlertTriangle, BookOpen, Clock, Tag, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PREMIUM_FEATURES = [
  { text: "50 messages IA / jour", icon: MessageSquare },
  { text: "20 simulations / jour", icon: FlaskConical },
  { text: "5 diagnostics / jour", icon: Stethoscope },
  { text: "Quiz illimités", icon: HelpCircle },
  { text: "Checklist interactive", icon: CheckSquare },
  { text: "Toutes les études de cas", icon: Library },
  { text: "Classification des risques", icon: AlertTriangle },
  { text: "Configuration LLM personnelle", icon: Sparkles },
  { text: "Support prioritaire", icon: Crown },
];

const FREE_FEATURES = [
  { text: "3 messages IA / jour", icon: MessageSquare },
  { text: "1 simulation au total", icon: FlaskConical },
  { text: "1 diagnostic au total", icon: Stethoscope },
  { text: "1 quiz au total", icon: HelpCircle },
  { text: "Checklist (lecture seule)", icon: CheckSquare },
  { text: "1 étude de cas", icon: Library },
  { text: "Glossaire complet", icon: BookOpen },
  { text: "Timeline complète", icon: Clock },
];

export default function PricingPage() {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState("annual"); // default to annual (best value)
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [validating, setValidating] = useState(false);
  const [loading, setLoading] = useState(false);
  const isPremium = user?.role === "premium" || user?.role === "admin";

  const PLANS = {
    monthly: { id: "monthly", label: "Mensuel", price: 9, period: "/mois", saved: 0 },
    annual: { id: "annual", label: "Annuel", price: 90, period: "/an", saved: 18, badge: "-17%" },
  };
  const current = PLANS[selectedPlan];
  const finalPrice = coupon
    ? coupon.final_amount
    : current.price;

  const validateCoupon = async () => {
    if (!couponCode.trim()) { setCoupon(null); return; }
    setValidating(true);
    try {
      const { data } = await API.post("/payments/preview-coupon", { code: couponCode.trim(), plan: selectedPlan });
      setCoupon(data);
      toast.success(`Code ${data.code} appliqué : -${data.discount_amount.toFixed(2)} €`);
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.detail || "Code promo invalide");
    } finally { setValidating(false); }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const payload = { origin_url: origin, plan: selectedPlan };
      if (coupon?.code) payload.coupon_code = coupon.code;
      const { data } = await API.post("/payments/create-checkout", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="pricing-page">
      <div className="text-center mb-8 animate-fade-in">
        <h1 className="text-3xl font-bold mb-2 text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Choisissez votre plan
        </h1>
        <p className="text-[var(--ia-text-muted)] max-w-md mx-auto">
          {isPremium ? "Vous êtes actuellement membre Premium." : "Passez en Premium pour un accès complet."}
        </p>
      </div>

      {/* Plan toggle */}
      {!isPremium && (
        <div className="flex justify-center mb-6 animate-fade-in">
          <div className="inline-flex p-1 rounded-full border border-[var(--ia-border)] bg-white" data-testid="plan-toggle">
            {Object.values(PLANS).map(p => (
              <button
                key={p.id}
                onClick={() => { setSelectedPlan(p.id); setCoupon(null); setCouponCode(""); }}
                data-testid={`plan-toggle-${p.id}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${selectedPlan === p.id ? "bg-[var(--ia-navy)] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                {p.label}
                {p.badge && <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{p.badge}</Badge>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Free plan card */}
        <Card data-testid="plan-free" className="border-[var(--ia-border)]">
          <CardHeader className="text-center pt-8">
            <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Gratuit</CardTitle>
            <div className="mt-3">
              <span className="text-4xl font-bold text-[var(--ia-text)]">0</span>
              <span className="text-lg font-bold">€</span>
            </div>
            <p className="text-sm text-[var(--ia-text-muted)] mt-2">Découvrez les essentiels</p>
          </CardHeader>
          <CardContent className="pt-4">
            <ul className="space-y-3 mb-6">
              {FREE_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm">
                  <f.icon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  <span className="text-slate-700">{f.text}</span>
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full" disabled data-testid="plan-free-btn">
              {user?.role === "free" ? "Plan actuel" : "—"}
            </Button>
          </CardContent>
        </Card>

        {/* Premium plan card */}
        <Card data-testid="plan-premium" className="border-[var(--ia-border)] ring-2 ring-[var(--ia-blue)] shadow-md relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-[var(--ia-blue)] text-white px-3"><Crown className="h-3 w-3 mr-1" /> Premium</Badge>
          </div>
          <CardHeader className="text-center pt-8">
            <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Premium {current.label}</CardTitle>
            <div className="mt-3">
              {coupon && (
                <div className="text-sm line-through text-[var(--ia-muted)] mb-1">{current.price.toFixed(2)} €</div>
              )}
              <span className="text-4xl font-bold text-[var(--ia-text)]" data-testid="premium-price">{finalPrice.toFixed(2)}</span>
              <span className="text-lg font-bold">€</span>
              <span className="text-[var(--ia-text-muted)]">{current.period}</span>
              {coupon && (
                <div className="text-xs text-emerald-600 font-semibold mt-1">-{coupon.discount_amount.toFixed(2)} € avec {coupon.code}</div>
              )}
            </div>
            {selectedPlan === "annual" && !coupon && (
              <p className="text-xs text-emerald-600 font-semibold mt-1">Économisez 18 € vs mensuel</p>
            )}
          </CardHeader>
          <CardContent className="pt-4">
            <ul className="space-y-3 mb-4">
              {PREMIUM_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm">
                  <f.icon className="h-4 w-4 flex-shrink-0 text-[var(--ia-blue)]" />
                  <span className="text-slate-700">{f.text}</span>
                </li>
              ))}
            </ul>

            {/* Coupon input */}
            {!isPremium && (
              <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-[var(--ia-border)]">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                  <Tag className="h-3.5 w-3.5" /> Code promo
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="ex. RGPD20"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); if (!e.target.value) setCoupon(null); }}
                    onKeyDown={(e) => e.key === "Enter" && validateCoupon()}
                    className="h-8 text-xs"
                    data-testid="coupon-input"
                  />
                  <Button onClick={validateCoupon} disabled={!couponCode.trim() || validating} size="sm" variant="outline" data-testid="coupon-apply-btn">
                    {validating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Appliquer"}
                  </Button>
                </div>
                {coupon && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-emerald-700 font-medium flex items-center gap-1"><Check className="h-3 w-3" /> {coupon.code} appliqué</span>
                    <button onClick={() => { setCoupon(null); setCouponCode(""); }} className="text-slate-500 hover:text-slate-700">Retirer</button>
                  </div>
                )}
              </div>
            )}

            <Button className="w-full bg-[var(--ia-navy)] hover:bg-[var(--ia-navy)]/90"
              onClick={handleUpgrade}
              disabled={isPremium || loading}
              data-testid="plan-premium-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
              {isPremium ? "Déjà Premium" : `Passer en Premium ${current.label}`}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="text-center mt-8 text-xs text-[var(--ia-muted)]">
        Paiement sécurisé par Stripe. Vous pouvez annuler à tout moment depuis votre profil.
      </div>
    </div>
  );
}
